// src/pages/ChangePasswordPage.jsx
import React, { useState } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import toast, { Toaster } from "react-hot-toast";
import { auth } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";

function friendlyAuthError(err) {
  const code = String(err?.code || "");
  if (code.includes("wrong-password")) return "Current password is incorrect.";
  if (code.includes("invalid-credential")) return "Current password is incorrect.";
  if (code.includes("weak-password")) return "New password is too weak. Use at least 6 characters.";
  if (code.includes("requires-recent-login")) {
    return "For security, sign out and sign in again, then try changing your password.";
  }
  return err?.message || "Could not update password.";
}

export default function ChangePasswordPage() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const user = auth.currentUser;
  const email = user?.email;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user || !email) {
      toast.error("No signed-in account with an email. Use your admin or a password reset link.");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      const cred = EmailAuthProvider.credential(email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated. Use your new password next time you sign in.");
    } catch (err) {
      console.error(err);
      toast.error(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900">Change my password</h1>
        <p className="mt-2 text-sm text-slate-600">
          Updates your <strong>Firebase</strong> sign-in password (email login). If you usually sign in with a username,
          use the same password — the app checks Firebase when needed.
        </p>

        {!email ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            This session has no email on the account. Sign out and sign in with email + password, or ask an admin to
            reset your password.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs text-slate-500">
              Signed in as <span className="font-medium text-slate-700">{email}</span>
            </p>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Current password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">New password</label>
              <input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Confirm new password</label>
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Update password"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
